// components/workspace-buttons/WorkspaceButton.tsx
import { buttonColorVariantSchema, buttonConfigurationSchema } from '@doss/common-all'
import { Loader } from 'lucide-react'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Button } from '~/@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/@/components/ui/tooltip'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DossIcon } from '~/components/DossIcon'
import { RecordDetailTableRowWorkspaceButton } from '~/components/records/types'
import { ConfirmationDialog } from '../ConfirmationDialog'

interface WorkspaceButtonProps {
  button: RecordDetailTableRowWorkspaceButton
  /** Action to execute when button is clicked */
  onExecute: (button: RecordDetailTableRowWorkspaceButton) => Promise<void>
  disabled?: boolean
  size?: 'default' | 'xs' | 'sm' | 'lg'
  /** Disables button and shows processing spinner instead of button */
  isProcessing?: boolean
}

export function WorkspaceButton({
  button,
  onExecute,
  disabled = false,
  isProcessing = false,
  size = 'xs',
}: WorkspaceButtonProps) {
  const { t } = useTypedTranslation()
  const [showConfirmation, setShowConfirmation] = useState(false)
  // Inbetween state from button click -> button processing to prevent flicker and double clicking
  const [isExecuting, setIsExecuting] = useState(false)

  const buttonConfig = useMemo(() => {
    // Handle null/undefined
    if (!button.buttonConfiguration) {
      return { colorVariant: buttonColorVariantSchema.enum.OUTLINE }
    }

    // Parse if it's a string
    const configToParse =
      typeof button.buttonConfiguration === 'string'
        ? JSON.parse(button.buttonConfiguration)
        : button.buttonConfiguration

    // Validate against schema
    const parseResult = buttonConfigurationSchema.safeParse(configToParse)

    // Return validated config or default
    return parseResult.success
      ? parseResult.data
      : { colorVariant: buttonColorVariantSchema.enum.OUTLINE }
  }, [button.buttonConfiguration])

  const isDestructive = buttonConfig.colorVariant === buttonColorVariantSchema.enum.DESTRUCTIVE

  const getButtonVariant = (colorVariant: string) => {
    switch (colorVariant) {
      case buttonColorVariantSchema.enum.OUTLINE:
        return 'outline'
      case buttonColorVariantSchema.enum.DESTRUCTIVE:
        return 'secondaryDestructive'
      default:
        return 'outline'
    }
  }

  // Reset isExecuting when isProcessing becomes true (parent has caught up)
  useEffect(() => {
    if (isProcessing) {
      setIsExecuting(false)
    }
  }, [isProcessing])

  const handleClick = () => {
    if (button.requiresConfirmation) {
      setShowConfirmation(true)
    } else {
      setIsExecuting(true)
      // Once the execute call settles, `isProcessing` owns the spinner. Without this reset a
      // fast-completing action could clear `isProcessing` before it ever rendered true here,
      // leaving `isExecuting` (and the spinner) stuck permanently (INC-1970).
      onExecute(button).finally(() => setIsExecuting(false))
      setShowConfirmation(false)
    }
  }

  const handleConfirm = async () => {
    // Close modal immediately
    setShowConfirmation(false)
    // Set executing state to keep button disabled
    setIsExecuting(true)

    try {
      // Then execute the action
      await onExecute(button)
    } finally {
      setIsExecuting(false)
    }
  }

  let startIcon: ReactNode | undefined
  if (isProcessing || isExecuting) {
    startIcon = <Loader className="size-3 animate-spin [animation-duration:2s]" />
  } else if (button.icon) {
    startIcon = <DossIcon name={button.icon.name} size={12} />
  }

  const buttonElement = (
    <span className="inline-block">
      <Button
        disabled={disabled || isProcessing || isExecuting}
        onClick={handleClick}
        size={size}
        startIcon={startIcon}
        variant={getButtonVariant(buttonConfig.colorVariant)}
      >
        {button.name}
      </Button>
    </span>
  )

  return (
    <>
      <Tooltip open={isProcessing || isExecuting ? undefined : false}>
        <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
        <TooltipContent className="cursor-pointer hover:underline">
          {t('table_settings.button_node.action_running', { actionName: button.name })}
        </TooltipContent>
      </Tooltip>

      <ConfirmationDialog
        checkboxText={
          button.requiresConfirmation
            ? (button.confirmationMessage ??
              t('table_settings.button_node.confirmation_message_placeholder'))
            : undefined
        }
        confirmLabel={t('common.actions.confirm')}
        description={button.manualTrigger?.action?.description ?? undefined}
        isLoading={isProcessing || isExecuting}
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirm}
        title={button.name}
        variant={isDestructive ? 'destructive' : 'default'}
      />
    </>
  )
}
